//Formatters
import { format } from 'svelte-i18n';
//Helpers
import {
	get_local_ip,
	isBluTRV,
	isWallDisplay,
	is_gen_2,
	is_gen_z,
	real_id,
	real_id_z,
} from './interface_helpers';
import compareVersions, { validate } from 'compare-versions';
import { getComponentByDevice, isVirtualDevice } from './virtual_helpers/virtual_components_helpers';
import { compareFWDates, compareFWVersions, parseFWVersion } from './firmware_compare_helpers';
//Stores
import { get } from 'svelte/store';
import { firmwares, firmwares_gen_2, updating_devices } from 'src/stores/device-runtime/process';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
//Services
import { localReq, postReq } from '../services/http';
import { gen2CloudReq, gen2Request } from '../services/gen_2_http_reqs';
//Constants
import {
	FW_MIN_VERSION_DIFFUSER,
	FW_MIN_VERSION_GEN2_DEFAULT,
	FW_MIN_VERSION_GEN2_DEFAULT_DATE,
	FW_MIN_VERSION_WALL_DISPLAY,
	FWVERSION19TS,
	FWVERSION110TS,
	HALF_SLEEPING_TYPES,
	SLEEPING_TYPES,
} from '../constants';
//Design system components
import { toast } from '@Shelly/shelly-design-system';
//Types
import type { Device } from '../types/Device';
import type { ControlUpdateRequest } from '../types/requests/control/control_update';
import type { Request } from '../types/requests/request';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import { ZWAVE_DEVICE_API } from './z_wave_qr_helpers';
import { isPeripheralDevice } from './addons/addon_device_helpers';

export function split_fw_ver_gen1(settings) {
	return settings?.fw?.split('/') || ['', ''];
}

export function get_fw_version_gen1(settings) {
	const split_fw_ver = split_fw_ver_gen1(settings);

	return split_fw_ver[1].indexOf('release') > -1
		? split_fw_ver[1].split('-')[1].split('@')[0]
		: split_fw_ver[1].split('-')[0].split('@')[0];

	// let v = version.substring(1).split('.');

	// if (version === 'Solus') {
	//     // Souls is a custom firmware, and can not be updated.
	//     return false;
	// }
}
export const get_supported_ts = (device) => {
	let supported_ts = FWVERSION19TS;

	switch (device.type) {
		case 'SHSW-22':
			supported_ts = 20191216;
			break;
		case 'SHSEN-1':
			supported_ts = 20200709;
			break;
		case 'SHSW-44':
			supported_ts = 20201019;
			break;
		case 'SHPLG-1':
			supported_ts = 20210115;
			break;
		case 'SHDW-2':
			supported_ts = FWVERSION110TS;
			break;
		case 'SHTRV-01':
		case 'SHMOS-01':
		case 'SHMOS-02':
			supported_ts = get(firmwares)[device.type].version.split('/')[0].split('-')[0];
			break;
		// case 'SHMOS-02':
		// 	supported_ts = $firmwares['SHMOS-02'].version
		// 		.split('/')[0]
		// 		.split('-')[0];
		// 	break;

		default:
			break;
	}
	return supported_ts;
};

export function verifyDeviceUnsupportedFWVersion(device, settings) {
	if (device?.type === 'SHSW-1S') {
		// SHSW-1S / Shelly Harvia RSS is a custom firmware, and can not be updated.
		return false;
	}

	const split_fw_ver = split_fw_ver_gen1(settings);
	const timestamp = split_fw_ver[0].split('-')[0];
	const supported_ts = get_supported_ts(device);
	if (!is_gen_2(device)) {
		// we support all gen 2 firmwares to display the device
		return timestamp < supported_ts;
		// unsupported = ( v[0]<1 || ( v[0]===1 && v[1]<9 ) ) || timestamp<supported_ts
	}
}
/**
 * Corner case for vendor-specific firmwares, e.g. Solus
 *
 * @param settings
 * @param vendor
 * @returns {boolean}
 */
export const isVendorSpecificFW = (settings: any, vendor: string): boolean => {
	return settings?.fw?.includes('/' + vendor + '@');
};

const extractGen1FWVersion = (settings: any): string => {
	const split_fw_ver = settings?.fw?.split('/') || ['', ''];
	let fwVersion = split_fw_ver[1].split('-')[0].split('@')[0];
	return fwVersion.startsWith('v') ? fwVersion.substring(1) : fwVersion;
};

const extractGen2FWVersion = (settings: any): string => {
	return settings?.DeviceInfo?.ver?.split('-')?.[0] || settings?.sys?.device?.fw_id || '0.0.1';
};

const extractGBLEFWVersion = (device: Device, statuses?: any): string => {
	const semVer = /\d+(\.\d+){2,}/gi;
	if (
		typeof device?.fwID !== 'undefined' &&
		typeof device?.fwID?.match(semVer)?.toString() !== 'undefined'
	) {
		return device?.fwID?.match(semVer)?.toString();
	} else {
		return statuses?.['fwversion:0']?.version || '0.0.1';
	}
};

export const verifySettingUnsupportedFWVersion = (
	device: Device,
	settings: any,
	minVersion: string | null,
	statuses?: any,
): { minVersion: string; fwVersion: string } | undefined => {
	if (!minVersion) {
		return undefined;
	}

	let fwVersion: string | undefined;

	try {
		if (device.gen === 'GBLE') {
			fwVersion = extractGBLEFWVersion(device, statuses);
		} else if (is_gen_2(device)) {
			fwVersion = extractGen2FWVersion(settings);
		} else {
			fwVersion = extractGen1FWVersion(settings);
		}
		if (fwVersion && compareFWVersions(fwVersion, '<', minVersion)) {
			return { minVersion, fwVersion };
		}
	} catch (error) {
		console.error(
			`compareVersions failed because of extracted semver: ${fwVersion}. Firmware string was: ${
				is_gen_2(device) ? settings?.DeviceInfo?.ver : settings?.fw
			}, and target supported semver was: ${minVersion}`,
		);
	}

	return undefined;
};

export const canUpdateFW = (device: Device): boolean => {
	if (SLEEPING_TYPES.includes(device.type) || HALF_SLEEPING_TYPES.includes(device.type)) {
		const deviceStatus = devices_status.getByRealId(device.id);
		if (is_gen_2(device)) {
			const devicepower = deviceStatus?.[`devicepower:${device.channel}`];
			if (devicepower?.external?.present) return true;
			return devicepower?.battery?.percent > 20;
		} else {
			return deviceStatus?.bat?.value > 20;
		}
	}
	return true;
};

export const updateFW = async (device: Device, target_fw: string = 'stable') => {
	if (isBluTRV(device?.type)) {
		return await updateFWBluTRV(device);
	} else if (is_gen_2(device)) {
		return await updateFWGen2(device, target_fw);
	} else if (is_gen_z(device)) {
		return await updateFWGenZ(device);
	} else {
		return await updateFWGen1(device, target_fw);
	}
};

const updateFWGen1 = async (device: Device, target_fw: string = 'stable') => {
	const id = real_id(device.id),
		local_ip = get_local_ip(id);
	let res: ControlUpdateRequest;
	if (local_ip) {
		const url = target_fw == 'stable' ? 'update=1' : `url=${target_fw}`;
		res = await localReq(device, `${local_ip}/ota?${url}`, 'post', {}, 10000, true);

		res.isok = res.isok && res.status == 'updating';

		if (
			!res.isok &&
			typeof get(cloud_disabled_devices)[device.id] === 'undefined' // is not cloud disabled
			// $auth_locked_devices.indexOf[real_id(device.id)] < 0 // is not in the auth locked list
		) {
			const data: { id: string; url?: string } = {
				id,
			};
			if (target_fw != 'stable') data.url = target_fw;
			res = await postReq(`/device/update`, data, 20000);
		}
	} else {
		const data: { id: string; url?: string } = {
			id,
		};
		if (target_fw != 'stable') data.url = target_fw;
		res = await postReq(`/device/update`, data, 20000);
	}
	onUpdateResponseFW(
		res,
		device,
		target_fw == 'stable' ? get(firmwares)?.[device?.type]?.version : target_fw,
		get(firmwares)?.[device?.type]?.version,
	);
	return res;
};

const updateFWGen2 = async (device: Device, target_fw: string = 'stable') => {
	const id = real_id(device.id);
	const firmwares = get(firmwares_gen_2);

	let url = target_fw;

	//Shelly Plus H&T
	if (target_fw == 'stable' && typeof firmwares[id]['stable'].url !== 'undefined') {
		url = firmwares[id]['stable'].url;
	}

	// special case for Wall Display adapting to Gen.2 versioning system:
	// more info in this Gitlab issue https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/739
	if (device.type === 'SAWD-0A1XX10EU1' && target_fw === 'custom') {
		url = 'https://repo.shelly.cloud/firmware/SAWD-0A1XX10EU1/stable/SAWD-0A1XX10EU1.zip';
	}

	const stage = /^(stable|test|beta)$/.test(target_fw),
		data = { url: url },
		res: Request = await gen2Request(device, 'Shelly.Update', data);

	onUpdateResponseFW(
		res,
		device,
		stage ? firmwares[id]?.[target_fw]?.build_id : url,
		firmwares[id]?.[target_fw]?.build_id,
	);
	/*
	const params = {
				id: real_id_z(device.id), //gateway's id
				a: 'call', //inclusion command
				cc: 'Configuration', //command class
				m: 'set', //method
				ep: 0, //endpoint
				params: JSON.stringify([
					{ parameter: +setting, value: +inputTypes[deviceChannel] }
				]) //parameters parameter - setting index, value - setting value
			};
	*/
	return res;
};

const updateFWGenZ = async (device: Device) => {
	const params = {
		id: real_id_z(device.id), //gateway's id
		a: 'fwup',
	};
	const res: SettingRequest = await postReq(ZWAVE_DEVICE_API, {
		...params,
	});
	onUpdateResponseFW(res, device, '');
	return res;
};

export const onUpdateResponseFW = async (
	res: SettingRequest,
	device: Device,
	target_fw: string,
	version: string = '',
) => {
	const $format = get(format);
	const isBluTRVDevice = isBluTRV(device.type);
	if (!res?.isok) {
		console.log('res?.errors?.code ', res?.errors?.code);
		//Pako - ToDo this codes needs to be checked and maybe documented somewhere
		if (res?.errors?.code == -106 || res?.errors?.code == -144) {
			toast.open({
				type: 'danger',
				title: $format('alert_error_fw_update_in_progress', {
					default: 'Firmware update is already in progress.',
				}),
			});
			if (!isBluTRVDevice) {
				handleUpdatingState(device, version);
			}
		} else if (res?.errors?.code == -114) {
			toast.open({
				type: 'danger',
				title: $format('alert_error_fw_update_not_found', {
					default: 'FW stage stable not found!',
				}),
			});
		} else if (res?.errors?.code == -107) {
			// Ads detail log message when OTA is not allowed.
			// Currently one of these is shown:
			// OTA not allowed. Battery below 30 percent.
			// OTA not allowed. Alarm detected.
			// OTA not allowed. Battery below 30 percent. Alarm detected.
			let title = $format('fw_ota_not_allowed_err_toast', {
				default: 'OTA not allowed.',
			});
			const message = res.errors?.message;
			if (/battery\s+below/i.test(message)) {
				title += $format('fw_ota_not_allowed_battery_err_toast', {
					default: 'Battery below 30 percent.',
				});
			}
			if (/alarm\s+detected/i.test(message)) {
				title += $format('fw_ota_not_allowed_alarm_err_toast', {
					default: 'Alarm detected.',
				});
			}
			toast.open({
				type: 'danger',
				title,
			});
		} else if (res?.errors?.['latest_firmware']) {
			toast.open({
				type: 'danger',
				title: $format('fw_update_latest_fw_err_toast', {
					default: 'This device already has the latest firmware version',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('fw_update_err_toast', {
					default:
						"Failed to update the device. Please check the device's internet connection.",
				}),
			});
		}
		return false;
	}
	// if (discovered_statuses?.[id]) {
	// 	discovered_statuses.update(data => {
	// 		delete data[id];
	// 		return data;
	// 	});
	// }
	// if (discovered_settings?.[id]) {
	// 	discovered_settings.update(data => {
	// 		delete data[id];
	// 		return data;
	// 	});
	// }
	if (!SLEEPING_TYPES.includes(device.type)) {
		if (!isBluTRVDevice) {
			handleUpdatingState(device, version);
		}

		// if (showToast) {
		toast.open({
			type: 'success',
			title: $format('fw_update_scc_toast', {
				default: 'Updating device... It will be responsive after 30-60 seconds.',
			}),
		});
		// }
		return get(updating_devices);
	} else {
		toast.open({
			type: 'success',
			title: $format('fw_update_sleeping_scc_toast', {
				default:
					'The device will update when it wakes up. You can "force" this by waking it up from the control button.',
			}),
		});
	}
};

// Always operate on the bare baseId in addition to any channel-suffixed entries
// in $devices. This is what the rest of the app reads from `updating_devices`
// (e.g. `isUpdating` in devices_states.ts and FirmwareUpdate.svelte both look
// up `updating_devices[real_id(device.id)]`), so without the baseId entry
// single-channel Gen 2/3 devices — whose only `$devices` key is `<base>_0` —
// never appear as "updating" on the main page.
export function handleUpdatingState(device: Device, version: string): void {
	const gen2 = is_gen_2(device);
	const baseId = real_id(device?.id);
	if (!baseId) return;
	const allDevices = get(devices);

	const writeUpdating = (id: string) => {
		updating_devices.update(id, { fw: version, ts: Date.now() });
		if (gen2 && typeof get(updating_devices)[id]?.prog === 'undefined') {
			updating_devices.updateProgress(id, 0);
		}
	};

	writeUpdating(baseId);
	for (const id in allDevices) {
		if (id !== baseId && new RegExp(`^${baseId}_\\d`).test(id) && !/_22\d{2}$/.test(id)) {
			writeUpdating(id);
		}
	}
}

export function handleUpdateProgress(device: Device, progress: number): void {
	const baseId = real_id(device?.id);
	if (!baseId) return;
	const allDevices = get(devices);

	updating_devices.updateProgress(baseId, progress);
	for (const id in allDevices) {
		if (id !== baseId && new RegExp(`^${baseId}_\\d`).test(id) && !/_22\d{2}$/.test(id)) {
			updating_devices.updateProgress(id, progress);
		}
	}
}

export function removeUpdatingState(device: Device): void {
	const baseId = real_id(device?.id);
	if (!baseId) return;
	const allDevices = get(devices);

	updating_devices.remove(baseId);
	for (const id in allDevices) {
		if (id !== baseId && new RegExp(`^${baseId}_\\d`).test(id) && !/_22\d{2}$/.test(id)) {
			updating_devices.remove(id);
		}
	}
}

/**
 * Handle BluTRV fw update progress and success real-time updates
 *
 * @param baseDeviceId    The device id of the parent device ( Gateway )
 * @param bthomeComponent The component id of the BluTRV device
 * @param progress
 * @param otaSuccess
 */
export const handleBluTRVUpdate = (
	baseDeviceId: string,
	bthomeComponent: string,
	progress: number,
	otaSuccess = false,
) => {
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(baseDeviceId);
	const trvComponent = Object.keys(deviceSettings || {})
		.filter((i) => /^(blutrv):2\d{2}$/.test(i))
		.find((i) => deviceSettings[i]?.trv == bthomeComponent);
	if (trvComponent) {
		const componentID = trvComponent.split(':')[1];
		const trvDeviceID = `${baseDeviceId}_2${componentID}`;
		if (!otaSuccess) {
			updating_devices.update(trvDeviceID, { fw: '', ts: Date.now() });
		}
		updating_devices.updateProgress(trvDeviceID, progress);
		if (otaSuccess) {
			setTimeout(() => {
				updating_devices.remove(trvDeviceID);
			}, 3000);
		}
	}
};

export const getTRVFirmwareVersion = (device: Device): string => {
	const component = getComponentByDevice(device);
	const devicesStatus = devices_status.getByRealId(device?.id);
	const trvStatus = devicesStatus?.[component];
	return devicesStatus?.[`blutrv_rinfo:${trvStatus?.id}`]?.device_info?.fw_id;
};

/**
 * Returns the firmware id of the BLU TRV's parent gateway device.
 *
 * @param device - The BLU TRV (virtual) device whose gateway version is needed.
 * @returns The gateway firmware id string (e.g. `20240911-180733/v2.0.0@...`) or `undefined`.
 */
export const getBluTRVGatewayFirmwareVersion = (device: Device): string => {
	return devices_settings.getByRealId(real_id(device?.id))?.sys?.device?.fw_id;
};

export const getDeviceMinFwVersion = (device: Device): string | undefined => {
	if (isVirtualDevice(device?.type) || isPeripheralDevice(device?.type) || isBluTRV(device?.type)) {
		return undefined;
	}
	if (FW_MIN_VERSION_DIFFUSER[device.type]) {
		return FW_MIN_VERSION_DIFFUSER[device.type];
	}
	if (isWallDisplay(device.type)) {
		return FW_MIN_VERSION_WALL_DISPLAY;
	}
	if (is_gen_2(device)) {
		return FW_MIN_VERSION_GEN2_DEFAULT;
	}
	return undefined;
};

/** Returns true when device fw is below FW_MIN_VERSION threshold → mandatory update required. */
export const isDeviceBelowMinFwVersion = (device: Device): boolean => {
	const minVersion = getDeviceMinFwVersion(device);
	if (!minVersion) {
		return false;
	}
	const fwId = devices_settings.getByRealId(device.id)?.sys?.device?.fw_id;
	if (!fwId) {
		return false;
	}

	if (is_gen_2(device) && minVersion === FW_MIN_VERSION_GEN2_DEFAULT) {
		const parsedFw = parseFWVersion(fwId);
		if (!parsedFw) {
			return false;
		}
		if (parsedFw.version) {
			return compareFWVersions(fwId, '<', minVersion);
		}
		return compareFWDates(fwId, '<', FW_MIN_VERSION_GEN2_DEFAULT_DATE);
	}

	return compareFWVersions(fwId, '<', minVersion);
};

// This function checks if a device requires a forced firmware update.
export const checkDeviceForceUpdate = (device: Device): boolean => {
	if (isVirtualDevice(device?.type) || isPeripheralDevice(device?.type)) {
		return false;
	}
	if (isBluTRV(device?.type)) {
		return compareFWDates(getTRVFirmwareVersion(device), '<=', '20241011');
	}
	if (device?.type === 'S3GW-1DBT001') {
		return compareFWDates(
			devices_settings.getByRealId(device.id)?.sys?.device?.fw_id,
			'<=',
			'20240911',
		);
	}
	if (device?.type === 'S4PB-00CU000002') {
		return compareFWDates(
			devices_settings.getByRealId(device.id)?.sys?.device?.fw_id,
			'<',
			'20260618',
		);
	}
	if (is_gen_2(device)) {
		return isDeviceBelowMinFwVersion(device);
	}
	return false;
};

export const checkTRVForceUpdateWithBootloader = (device: Device): boolean => {
	if (isBluTRV(device?.type)) {
		return compareFWDates(getTRVFirmwareVersion(device), '<=', '20240911');
	}
	return false;
};

/**
 * Remove the updating state of a BluTRV device
 *
 * @param baseDeviceId 		The device id of the parent device ( Gateway )
 * @param bthomeComponent 		The component id of the BluTRV device
 * @param otaError 				Optional error message to display in a toast
 */
export const removeTRVUpdatingState = (
	baseDeviceId: string,
	bthomeComponent: string,
	otaError: string = null,
) => {
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(baseDeviceId);
	const trvComponent = Object.keys(deviceSettings || {})
		.filter((i) => /^(blutrv):2\d{2}$/.test(i))
		.find((i) => deviceSettings[i]?.trv == bthomeComponent);
	if (trvComponent) {
		const trvDeviceID = `${baseDeviceId}_2${trvComponent.split(':')[1]}`;
		if (get(updating_devices)?.[trvDeviceID]) {
			updating_devices.remove(trvDeviceID);
			if (otaError != null) {
				toast.open({ type: 'danger', title: otaError });
			}
		}
	}
};

const updateFWBluTRV = async (device: Device) => {
	const params: { id: number; bootloader?: boolean } = {
		id: Number(device.channel) - 2000,
		...(checkTRVForceUpdateWithBootloader(device) && { bootloader: true }),
	};
	const res = await gen2CloudReq(
		real_id(device.id),
		device.type,
		'BluTrv.UpdateFirmware',
		params,
		true,
	);
	if (res?.isok) {
		toast.open({
			type: 'success',
			title: get(format)('command_is_sent_to_the_device', {
				default: 'The command is sent to the device.',
			}),
		});
		updating_devices.update(device.id, { fw: '', ts: Date.now() });
		updating_devices.updateProgress(device.id, 0);
	} else {
		onUpdateResponseFW(res, device, '', '');
	}
	return res;
};
